import type { DateTime } from 'luxon'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { filterByMarketProgram, filterByType } from '@/features/bessDashboard/utils/chart'

/**
 * Energy constant used in opportunity cost calculation (€/MWh)
 * Formula: per hour = min(fcrd_up + fcrd_down, fcrn + ENERGY_CONSTANT) * DISCOUNT_FACTOR
 */
const ENERGY_CONSTANT = 2.68
const DISCOUNT_FACTOR = 0.8

export type HourlyOpportunityCost = {
  hour: number
  value: number
}

export type OpportunityCostResult = {
  /** Hourly opportunity cost values for charting */
  hourly: HourlyOpportunityCost[]
  /** Daily total (sum of all hourly values) */
  daily: number
}

/**
 * Extract hourly mean prices from forecast data for a specific market
 */
function extractHourlyMeanPrices(
  forecastsData: FormattedRow[] | undefined,
  market: string,
  selectedDeliveryDay: DateTime,
): Map<number, number> {
  const hourlyPrices = new Map<number, number>()

  if (!forecastsData || forecastsData.length === 0) {
    return hourlyPrices
  }

  // Filter by market program
  const marketData = filterByMarketProgram(forecastsData, market)

  // Filter by price forecast type
  const priceData = filterByType(marketData, 'capacity_price_per_MW_h_HOURLY')

  if (priceData.length === 0) {
    return hourlyPrices
  }

  // Format the selected date to match the forecast data format
  const targetDateStr = selectedDeliveryDay.toFormat('yyyy-MM-dd')

  // Find data points for the selected date
  const dayData = priceData.filter((row) => {
    const rowDateStr = String(row.ds).substring(0, 10)
    return rowDateStr === targetDateStr
  })

  // Extract mean price for each hour (using UTC hours for consistency)
  for (const row of dayData) {
    const timestamp = String(row.ds)
    const hour = new Date(timestamp).getUTCHours()
    const meanPrice = Number(row.mean)

    if (!Number.isNaN(meanPrice)) {
      hourlyPrices.set(hour, meanPrice)
    }
  }

  return hourlyPrices
}

/**
 * Calculate opportunity cost using the formula:
 * - Per hour: min(fcrd_up + fcrd_down, fcrn + ENERGY_CONSTANT) * DISCOUNT_FACTOR
 * - Daily: sum of all hourly values
 */
export function calculateOpportunityCosts(
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime,
): OpportunityCostResult | undefined {
  const fcrDUpPrices = extractHourlyMeanPrices(forecastsData, 'FCR-D UP', selectedDeliveryDay)
  const fcrDDownPrices = extractHourlyMeanPrices(forecastsData, 'FCR-D DOWN', selectedDeliveryDay)
  const fcrNPrices = extractHourlyMeanPrices(forecastsData, 'FCR-N', selectedDeliveryDay)

  // Need at least some data to calculate
  if (fcrDUpPrices.size === 0 && fcrDDownPrices.size === 0 && fcrNPrices.size === 0) {
    return undefined
  }

  const hourly: HourlyOpportunityCost[] = []
  let dailyTotal = 0

  // Calculate for each hour (0-23)
  for (let hour = 0; hour < 24; hour++) {
    const fcrDUp = fcrDUpPrices.get(hour) ?? 0
    const fcrDDown = fcrDDownPrices.get(hour) ?? 0
    const fcrN = fcrNPrices.get(hour) ?? 0

    // Formula: min(fcrd_up + fcrd_down, fcrn + energy_constant) * DISCOUNT_FACTOR
    const fcrDSum = fcrDUp + fcrDDown
    const fcrNWithConstant = fcrN + ENERGY_CONSTANT
    const hourlyValue = Math.min(fcrDSum, fcrNWithConstant) * DISCOUNT_FACTOR

    hourly.push({
      hour,
      value: Math.round(hourlyValue * 100) / 100,
    })

    dailyTotal += hourlyValue
  }

  return {
    hourly,
    daily: Math.round(dailyTotal * 10) / 10,
  }
}
